import moment from 'moment';
import Fuse from 'fuse.js';
export const genderDictionary = ['m', 'f', 'masculino', 'femenino', 'male', 'female', 'hombre', 'mujer'];
export const documentTypeDictionary = ['dni', 'rut', 'pasaporte', 'pas', 'ce', 'carnet', 'extranjeria', 'extranjería', 'cédula', 'cedula', 'identificador'];
export const thresholds = {
  documentNumberDensity: 0.8, 
  dateValidation: 0.8,        
  fuseSimilarity: 0.3,        
  evalMatchScore: 0.4,        
  staticVocabColumn: 0.6,     
};
export const documentNumberRegex = /^([a-zA-Z0-9\-.]{7,20})$/;
export const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
export const phoneRegex = /^\+?[0-9\s-]{6,20}$/;
export const calculateNumericDensity = (value) => {
  if (!value) return 0;
  const str = String(value).trim();
  if (str.length === 0) return 0;
  const numChars = str.replace(/[^0-9]/g, '').length;
  return numChars / str.length;
};
export const calculateCardinality = (columnValues) => {
  const valid = columnValues.filter(value => value !== null && value !== undefined && String(value).trim() !== '');
  if (valid.length === 0) return 0;
  const unique = new Set(valid.map(value => String(value).trim().toLowerCase()));
  return unique.size / valid.length;
};
export const evaluateDateType = (samples) => {
  let validMoments = 0;
  let isMostlyPastDate = false; 
  const dateStringRegex = /^\d{1,4}[-/]\d{1,2}[-/]\d{1,4}/;
  for (const sample of samples) {
    if (!sample) continue;
    const str = String(sample).trim();
    if (!dateStringRegex.test(str) && !str.includes('T')) continue;
    const parsedDate = moment(str);
    if (parsedDate.isValid()) {
      validMoments++;
      if (parsedDate.year() <= 2025 && parsedDate.year() > 1900) {
        isMostlyPastDate = true;
      }
    }
  }
  if (validMoments > 0 && validMoments >= samples.length * thresholds.dateValidation) {
    return isMostlyPastDate ? 'birthDate' : 'reservationDate';
  }
  return null;
};
export const resolveStaticVocabularies = (samples, evaluationTypes) => {
  const evalDict = Object.values(evaluationTypes)
    .flat()
    .map(evalType => (evalType.label || evalType.text).toLowerCase());
  const options = { threshold: thresholds.fuseSimilarity, includeScore: true };
  const fuse = new Fuse(evalDict.map(label => ({ label })), { keys: ['label'], ...options });
  let matchesEval = 0;
  let matchesGender = 0;
  let matchesDocType = 0;
  for (const sample of samples) {
    if (!sample) continue;
    const str = String(sample).trim().toLowerCase();
    const res = fuse.search(str);
    if (res.length > 0 && res[0].score < thresholds.evalMatchScore) matchesEval++;
    if (genderDictionary.includes(str)) matchesGender++;
    if (documentTypeDictionary.includes(str)) matchesDocType++;
  }
  const threshold = samples.length * thresholds.staticVocabColumn;
  if (matchesEval >= threshold) return 'evaluationType';
  if (matchesGender >= threshold) return 'gender';
  if (matchesDocType >= threshold) return 'documentType';
  return null;
};
export const groupParticles = (words) => {
  const particles = ['de', 'la', 'del', 'los', 'las', 'san', 'santa'];
  const result = [];
  for (let i = 0; i < words.length; i++) {
    const word = words[i].toLowerCase();
    if (particles.includes(word) && i + 1 < words.length) {
      words[i + 1] = words[i] + ' ' + words[i + 1];
    } else {
      result.push(words[i]);
    }
  }
  return result;
};
export const splitLatinoamericano = (fullName) => {
  if (!fullName) return { names: '', lastnames: '' };
  const rawWords = String(fullName).trim().split(/\s+/);
  if (rawWords.length === 0) return { names: '', lastnames: '' };
  if (rawWords.length === 1) return { names: rawWords[0], lastnames: '' };
  const blocks = groupParticles(rawWords);
  if (blocks.length === 1) {
    return { names: blocks[0], lastnames: '' };
  } else if (blocks.length === 2) {
    return { names: blocks[0], lastnames: blocks[1] };
  } else {
    const lastnames = blocks.slice(-2).join(' ');
    const names = blocks.slice(0, -2).join(' ');
    return { names, lastnames };
  }
};
export const resolveFuzzyHeaders = (orphanColumns, missingFields, t) => {
  const dictionary = [];
  missingFields.forEach(field => {
    let rawLabel = t(`reservations.massiveCharge.fields.${field.key}`).toLowerCase();
    rawLabel = rawLabel.replace(/\([^)]*\)/g, '').trim(); 
    const aliases = [rawLabel];
    if (field.key === 'reservationDate') aliases.push('fecha de reserva', 'fecha programacion', 'fecha de programacion', 'fecha');
    if (field.key === 'applicant') aliases.push('correo solicitante', 'email solicitante', 'correo', 'email', 'e-mail');
    if (field.key === 'documentNumber') aliases.push('rut', 'dni', 'documento', 'rut paciente', 'dni paciente', 'pasaporte', 'carnet', 'identificador');
    if (field.key === 'names') aliases.push('nombre', 'nombres', 'nombre paciente', 'nombres paciente', 'nombres pac');
    if (field.key === 'lastnames') aliases.push('apellido', 'apellidos', 'apellido paciente', 'apellidos paciente');
    if (field.key === 'birthDate') aliases.push('nacimiento', 'fecha nacimiento', 'fecha de nacimiento', 'f. nacimiento');
    if (field.key === 'project') aliases.push('proyecto', 'obra');
    if (field.key === 'jobPosition') aliases.push('cargo', 'puesto', 'rol', 'posicion');
    if (field.key === 'protocol') aliases.push('protocolo', 'bateria', 'examen', 'examenes', 'baterias');
    if (field.key === 'gender') aliases.push('genero', 'sexo');
    if (field.key === 'documentType') aliases.push('tipo documento', 'tipo rut', 'tipo dni');
    if (field.key === 'area') aliases.push('area', 'departamento', 'sector');
    aliases.forEach(alias => {
        dictionary.push({ key: field.key, alias: alias.toLowerCase() });
    });
  });
  const fuse = new Fuse(dictionary, { keys: ['alias'], threshold: 0.35, includeScore: true });
  const newAssignments = {};
  orphanColumns.forEach(col => {
     const cleanCol = col.toString().toLowerCase().replace(/[./]/g, ' ').replace(/\s+/g, ' ').trim();
     const res = fuse.search(cleanCol);
     if (res.length > 0 && res[0].score <= 0.35) {
        const matchedField = res[0].item.key;
        if (matchedField === 'protocol') {
           if (!newAssignments[matchedField]) newAssignments[matchedField] = [];
           newAssignments[matchedField].push(col);
        } else if (!newAssignments[matchedField]) {
           newAssignments[matchedField] = col;
        }
     }
  });
  return newAssignments;
};
